import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Waves, Settings2, Palette, Activity, Zap, Lock, ChevronRight } from 'lucide-react';
import { SimulationConfig } from '@/types';
import { SETTING_INFO } from '@/data/settingInfo';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';
import { SectionLabel } from './SectionLabel';

interface AudioSpectrumSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  plan?: string;
  onUpgradeClick?: () => void;
}

export const AudioSpectrumSection: React.FC<AudioSpectrumSectionProps> = ({ config, setConfig, isLocked, plan, onUpgradeClick }) => {
  // Compatible modes: SPAWNER, DRAWER, RHYTHMIC
  const COMPATIBLE_MODES = ['SPAWNER', 'DRAWER', 'RHYTHMIC'];
  const isCompatible = COMPATIBLE_MODES.includes(config.mode);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {!isCompatible && (
        <div className={`p-4 rounded-2xl border flex items-start gap-4 mb-2 ${
          config.darkMode 
            ? 'bg-white/5 border-white/10' 
            : 'bg-zinc-50 border-zinc-200 shadow-sm'
        }`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            config.darkMode ? 'bg-white/10' : 'bg-white shadow-sm'
          }`}>
            <Lock className="w-5 h-5 text-cyan-500" />
          </div>
          <div className="space-y-1">
            <p className={`text-[11px] font-black uppercase tracking-widest ${
              config.darkMode ? 'text-white' : 'text-zinc-950'
            }`}>
              Mode Level Lock
            </p>
            <p className={`text-[10px] leading-relaxed font-medium ${
              config.darkMode ? 'text-cyan-400/60' : 'text-cyan-700'
            }`}>
              Visualizer Bar settings are only compatible with <span className={config.darkMode ? 'text-cyan-400 font-black underline decoration-cyan-400/30' : 'text-cyan-600 font-black underline decoration-cyan-600/30'}>Spawner, Drawer, and Rhythmic</span> modes. 
              Change the simulation mode to unlock these features.
            </p>
          </div>
        </div>
      )}

      {isLocked && (
        <div 
          onClick={onUpgradeClick}
          className={`p-4 rounded-2xl border flex items-center justify-between gap-4 mb-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-95 group ${
            config.darkMode 
              ? 'bg-cyan-500/10 border-cyan-500/20' 
              : 'bg-cyan-50 border-cyan-100 shadow-sm'
          }`}
        >
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center text-white shadow-lg shadow-cyan-500/20">
                <Lock className="w-4 h-4" />
             </div>
             <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-widest leading-none">Upgrade to {plan || 'PRO'}</span>
                <span className="text-[8px] font-bold opacity-50 uppercase mt-1">Access pro visualizer studio</span>
             </div>
          </div>
          <ChevronRight className="w-4 h-4 opacity-20 group-hover:opacity-100 transition-opacity" />
        </div>
      )}

      <div className={`space-y-8 transition-all duration-300 ${(!isCompatible || isLocked) ? 'opacity-40 pointer-events-none grayscale-[0.5]' : ''}`}>
        {/* 1. Main Enable Toggle */}
        <ControlGroup label="Enable Spectrum" settingKey="rhythmicSpectrumEnabled" darkMode={config.darkMode}>
          <Toggle 
            active={config.rhythmicSpectrumEnabled} 
            onClick={() => setConfig(p => ({...p, rhythmicSpectrumEnabled: !p.rhythmicSpectrumEnabled}))} 
            color="bg-cyan-600" 
          />
        </ControlGroup>

        <AnimatePresence>
          {config.rhythmicSpectrumEnabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-8 overflow-hidden"
            >
            {/* 2. Color Mode Selection */}
            <div className="space-y-4">
              <SectionLabel darkMode={config.darkMode}>Color & Aesthetics</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                {(['BALL', 'RAINBOW', 'CUSTOM', 'RGB_PULSE', 'HUE_SHIFT'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setConfig(p => ({ ...p, rhythmicSpectrumColorMode: mode }))}
                    className={`py-3 rounded-xl border text-[9px] font-medium uppercase tracking-[0.15em] transition-all border ${config.rhythmicSpectrumColorMode === mode 
                      ? (config.darkMode ? 'bg-white border-white text-zinc-950 shadow-lg' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-md') 
                      : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-zinc-50 border-zinc-100 text-zinc-400 hover:bg-zinc-100')}`}
                  >
                    {mode.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
              
              {config.rhythmicSpectrumColorMode === 'CUSTOM' && (
                <ControlGroup label="Custom Bar Color" settingKey="rhythmicSpectrumCustomColor" darkMode={config.darkMode}>
                  <div className="flex items-center gap-3">
                    <input 
                      type="color" 
                      value={config.rhythmicSpectrumCustomColor || '#06b6d4'} 
                      onChange={e => setConfig(p => ({...p, rhythmicSpectrumCustomColor: e.target.value}))}
                      className="w-10 h-10 rounded-lg border-2 border-white/10 bg-transparent cursor-pointer"
                    />
                    <span className="text-[10px] font-mono font-bold opacity-40 uppercase">{config.rhythmicSpectrumCustomColor}</span>
                  </div>
                </ControlGroup>
              )}
            </div>

            {/* 3. Geometry & Layout */}
            <div className="space-y-4">
              <SectionLabel darkMode={config.darkMode}>Bar Geometry</SectionLabel>
              <div className="grid grid-cols-1 gap-1">
                <SmartInput 
                  label="Number of Bars" 
                  min={8} max={256} step={8} 
                  value={config.rhythmicSpectrumBarCount} 
                  onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarCount: v}))} 
                  darkMode={config.darkMode} 
                  settingKey="rhythmicSpectrumBarCount"
                />
                <div className="grid grid-cols-2 gap-4">
                  <SmartInput 
                    label="Min Height" 
                    min={0} max={100} 
                    value={config.rhythmicSpectrumMinHeight} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumMinHeight: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumMinHeight"
                  />
                  <SmartInput 
                    label="Max Height" 
                    min={20} max={500} 
                    value={config.rhythmicSpectrumMaxHeight} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumMaxHeight: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumMaxHeight"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <SmartInput 
                    label="Bar Thickness" 
                    min={1} max={50} 
                    value={config.rhythmicSpectrumBarWidth} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarWidth: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumBarWidth"
                  />
                  <SmartInput 
                    label="Bar Spacing" 
                    min={0} max={100} 
                    value={config.rhythmicSpectrumBarSpacing} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarSpacing: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumBarSpacing"
                  />
                </div>
              </div>
            </div>

            {/* 4. Dynamics & Reaction */}
            <div className="space-y-4">
              <SectionLabel darkMode={config.darkMode}>Dynamics & Response</SectionLabel>
              <div className="grid grid-cols-1 gap-1">
                <SmartInput 
                  label="Audio Sensitivity" 
                  min={0.1} max={5} step={0.1} 
                  value={config.rhythmicSpectrumAudioSensitivity} 
                  onChange={v => setConfig(p => ({...p, rhythmicSpectrumAudioSensitivity: v}))} 
                  darkMode={config.darkMode} 
                  settingKey="rhythmicSpectrumAudioSensitivity"
                />
                <div className="grid grid-cols-2 gap-4">
                  <SmartInput 
                    label="Grow Speed" 
                    min={0.01} max={1} step={0.01} 
                    value={config.rhythmicSpectrumGrowRate} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumGrowRate: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumGrowRate"
                  />
                  <SmartInput 
                    label="Shrink Speed" 
                    min={0.01} max={1} step={0.01} 
                    value={config.rhythmicSpectrumShrinkRate} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumShrinkRate: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumShrinkRate"
                  />
                </div>
                <SmartInput 
                    label="Decay Time (ms)" 
                    min={100} max={5000} step={100} 
                    value={config.rhythmicSpectrumDuration || 800} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumDuration: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumDuration"
                  />
              </div>
            </div>

            {/* 5. Interaction & Spatiality */}
            <div className="space-y-4">
              <SectionLabel darkMode={config.darkMode}>Interaction</SectionLabel>
              <div className="grid grid-cols-1 gap-1">
                <ControlGroup label="Global Audio" settingKey="rhythmicSpectrumGlobalAudio" darkMode={config.darkMode}>
                   <Toggle 
                      active={config.rhythmicSpectrumGlobalAudio} 
                      onClick={() => setConfig(p => ({...p, rhythmicSpectrumGlobalAudio: !p.rhythmicSpectrumGlobalAudio}))} 
                      color="bg-purple-600" 
                   />
                </ControlGroup>
                
                <SmartInput 
                  label="Audio Spatiality" 
                  min={0} max={1} step={0.01} 
                  value={config.rhythmicSpectrumAudioSpatiality} 
                  onChange={v => setConfig(p => ({...p, rhythmicSpectrumAudioSpatiality: v}))} 
                  darkMode={config.darkMode} 
                  settingKey="rhythmicSpectrumAudioSpatiality"
                />
                <div className="grid grid-cols-2 gap-4">
                  <SmartInput 
                    label="Collision Impact" 
                    min={0} max={5} step={0.1} 
                    value={config.rhythmicSpectrumCollisionIntensity} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumCollisionIntensity: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumCollisionIntensity"
                  />
                  <SmartInput 
                    label="Impact Spread" 
                    min={1} max={50} step={1} 
                    value={config.rhythmicSpectrumCollisionSpread} 
                    onChange={v => setConfig(p => ({...p, rhythmicSpectrumCollisionSpread: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicSpectrumCollisionSpread"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};
